import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Typography, Box, Button, Paper } from '@mui/material';
import { RootState } from '../../store/store';
import { joinEventAsync, leaveEventAsync } from '../../store/slices/authSlice';
import type { AppDispatch } from '../../store/store';

const HomeDebug: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated, joinedEvents } = useSelector((state: RootState) => state.auth);
  const [debugInfo, setDebugInfo] = useState<any>({});

  useEffect(() => {
    const updateDebugInfo = () => {
      const localStorageData = {
        token: localStorage.getItem('crowdtrades_token'),
        user: localStorage.getItem('crowdtrades_user'),
        joinedEvents: localStorage.getItem('crowdtrades_joined_events')
      };

      setDebugInfo({
        isAuthenticated,
        user: user ? {
          id: user.id,
          firstName: user.firstName,
          email: user.email
        } : null,
        joinedEvents,
        joinedEventsCount: joinedEvents.length,
        joinedEventsString: JSON.stringify(joinedEvents),
        localStorage: localStorageData
      });
      
      console.log('🔍 Redux Auth State Debug:', {
        isAuthenticated,
        user,
        joinedEvents,
        joinedEventsCount: joinedEvents.length
      });

      console.log('🔍 Raw localStorage Debug:', localStorageData);
    };

    updateDebugInfo();
    
    // Update every 2 seconds to catch changes
    const interval = setInterval(updateDebugInfo, 2000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user, joinedEvents]);

  const handleTestJoin = () => {
    if (!user?.id) {
      console.log('❌ No user ID available for testing');
      return;
    }
    
    // Test joining a fake event
    const testEventId = 'test_event_123';
    console.log('🧪 Testing join event action for:', testEventId);
    dispatch(joinEventAsync({ eventId: testEventId, userId: user.id }));
  };

  const handleTestLeave = () => {
    if (!user?.id) {
      console.log('❌ No user ID available for testing');
      return;
    }
    
    // Test leaving the first joined event
    if (joinedEvents.length > 0) {
      const eventToLeave = joinedEvents[0];
      console.log('🧪 Testing leave event action for:', eventToLeave);
      dispatch(leaveEventAsync({ eventId: eventToLeave, userId: user.id }));
    } else {
      console.log('⚠️ No joined events to test leaving');
    }
  };

  const handleRefreshLocalStorage = () => {
    console.log('🔄 Checking localStorage contents...');
    const keys = ['crowdtrades_token', 'crowdtrades_user', 'crowdtrades_joined_events'];
    keys.forEach(key => {
      const value = localStorage.getItem(key);
      console.log(`${key}:`, value);
    });
  };

  const handleClearJoinedEvents = () => {
    console.log('🧹 Clearing joined events from localStorage...');
    localStorage.removeItem('crowdtrades_joined_events');
    window.location.reload();
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        🔍 Joined Events Debug Page
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Authentication State:</Typography>
        <Typography>Authenticated: {debugInfo.isAuthenticated ? '✅ Yes' : '❌ No'}</Typography>
        {debugInfo.user ? (
          <Box sx={{ mt: 1 }}>
            <Typography>User ID: {debugInfo.user.id}</Typography>
            <Typography>Name: {debugInfo.user.firstName}</Typography>
            <Typography>Email: {debugInfo.user.email}</Typography>
          </Box>
        ) : (
          <Typography>User: ❌ Not logged in</Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Redux Joined Events (DATABASE-BASED):</Typography>
        <Typography>Count: {debugInfo.joinedEventsCount}</Typography>
        <Typography>Array: {debugInfo.joinedEventsString}</Typography>
        
        {debugInfo.joinedEvents && debugInfo.joinedEvents.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Individual Events:</Typography>
            {debugInfo.joinedEvents.map((eventId: string, index: number) => (
              <Typography key={index}>• {eventId}</Typography>
            ))}
          </Box>
        ) : (
          <Typography sx={{ mt: 2, color: 'warning.main' }}>
            ⚠️ No joined events found in Redux state
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>localStorage Contents (LEGACY - Should be empty):</Typography>
        <Typography>
          crowdtrades_token: {debugInfo.localStorage?.token ? `✅ Present (${debugInfo.localStorage.token.substring(0, 20)}...)` : '❌ Missing'}
        </Typography>
        <Typography>
          crowdtrades_user: {debugInfo.localStorage?.user ? '✅ Present' : '❌ Missing'}
        </Typography>
        <Typography sx={{ color: debugInfo.localStorage?.joinedEvents ? 'warning.main' : 'success.main' }}>
          crowdtrades_joined_events: {debugInfo.localStorage?.joinedEvents || 'null/missing (CORRECT - should be empty!)'}
        </Typography>
        
        {debugInfo.localStorage?.user && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2">User data sample:</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
              {debugInfo.localStorage.user.substring(0, 100)}...
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Test Actions:</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={handleTestJoin} disabled={!user?.id}>
            🧪 Test Join Event
          </Button>
          <Button variant="outlined" onClick={handleTestLeave} disabled={!user?.id}>
            🧪 Test Leave Event  
          </Button>
          <Button variant="outlined" onClick={handleRefreshLocalStorage}>
            🔄 Check localStorage
          </Button>
          <Button variant="outlined" color="warning" onClick={handleClearJoinedEvents}>
            🧹 Clear Joined Events (Legacy)
          </Button>
        </Box>
        
        {!user?.id && (
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            ⚠️ Please log in to test join/leave actions
          </Typography>
        )}
      </Paper>

      <Box sx={{ textAlign: 'center' }}>
        <Button variant="contained" href="/">
          ← Back to Homepage
        </Button>
      </Box>
    </Container>
  );
};

export default HomeDebug;